/**
 * Serato crate / M3U8 parser.
 *
 * Serato DJ Pro exports crates as extended M3U8 playlists — a well-known
 * open format that we can parse client-side without shipping any Serato
 * binary format hacks. To export from Serato:
 *   right-click on any crate → Export → Playlist as .m3u8
 *
 * Format sample:
 *   #EXTM3U
 *   #EXTINF:245,Artist Name - Track Title
 *   #EXTBPM:128.5                          (Serato-specific, optional)
 *   /Users/dj/Music/Track.mp3
 *   #EXTINF:198,Another Artist - Another Title
 *   /Users/dj/Music/Track2.mp3
 *
 * The file paths are absolute on the DJ's own drive. We can't read them
 * directly from the browser sandbox — so we surface them as a track list
 * with metadata (title, artist, BPM, duration) and let the DJ drop the
 * actual audio files afterward to match by filename.
 *
 * Also supports the shorter `.m3u` format (no #EXT prefix) as a fallback.
 */

const EXTINF_RE = /^#EXTINF:\s*(-?\d+(?:\.\d+)?)\s*,\s*(.*)$/i;
const EXTBPM_RE = /^#EXTBPM:\s*(-?\d+(?:\.\d+)?)/i;
const EXTKEY_RE = /^#EXTKEY:\s*(.+)$/i;   // Camelot / harmonic — Serato adds this sometimes
const EXTGENRE_RE = /^#EXTGENRE:\s*(.+)$/i;

/**
 * Parse an M3U/M3U8 crate export into a structured list.
 * @param {string} text — full file contents
 * @returns {{ crate_name: string, count: number, tracks: Array<TrackMeta> }}
 */
export function parseM3U(text) {
  const lines = text.split(/\r?\n/);
  let crateName = "Imported crate";
  const tracks = [];
  let pending = null;

  for (let raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith("#EXTM3U")) continue;
    if (line.startsWith("#PLAYLIST:")) {
      crateName = line.slice("#PLAYLIST:".length).trim() || crateName;
      continue;
    }

    const inf = line.match(EXTINF_RE);
    if (inf) {
      const durationSec = parseFloat(inf[1]);
      // Title format is usually "Artist - Title" but some Serato exports do
      // "Title" only. Split on the FIRST " - " so track names with dashes
      // don't split incorrectly.
      const raw = inf[2].trim();
      let artist = "", title = raw;
      const dashIdx = raw.indexOf(" - ");
      if (dashIdx > 0) {
        artist = raw.slice(0, dashIdx).trim();
        title = raw.slice(dashIdx + 3).trim();
      }
      pending = {
        artist, title,
        duration_seconds: Number.isFinite(durationSec) && durationSec > 0 ? durationSec : null,
        bpm: null, key: null, genre: null, file_path: null,
      };
      continue;
    }

    const bpm = line.match(EXTBPM_RE);
    if (bpm && pending) { pending.bpm = parseFloat(bpm[1]) || null; continue; }
    const key = line.match(EXTKEY_RE);
    if (key && pending) { pending.key = key[1].trim() || null; continue; }
    const gen = line.match(EXTGENRE_RE);
    if (gen && pending) { pending.genre = gen[1].trim() || null; continue; }

    // Other unknown #EXT lines — Serato writes a few proprietary ones, skip them silently.
    if (line.startsWith("#")) continue;

    // Non-# line = file path. Attach it to the pending track or synthesize
    // a placeholder if the crate lacks EXTINF metadata (bare .m3u fallback).
    if (!pending) pending = { artist: "", title: "", duration_seconds: null, bpm: null, key: null, genre: null };
    pending.file_path = line;
    // Extract a filename in case the DJ needs to match by name later
    pending.filename = line.split(/[/\\]/).pop() || line;
    if (!pending.title) pending.title = pending.filename.replace(/\.[^.]+$/, "");
    tracks.push(pending);
    pending = null;
  }

  return { crate_name: crateName, count: tracks.length, tracks };
}

/**
 * Storage helpers so imported crates persist across sessions.
 *
 * Historical note: v1 lived in localStorage capped at 25 crates and ~5 MB
 * total — far too small for a real DJ library. If you drop a Serato /
 * Rekordbox / Traktor / VirtualDJ collection with more than ~200 tracks
 * (bpm + hot cues per track ≈ 500 B) you'd blow the localStorage quota
 * and the "quota exceeded" catch below would silently discard your
 * import.
 *
 * v2 (current): the localStorage path is preserved as a SYNC fallback
 * for tiny reads (crate list on first render), but every write now
 * *also* fans out to IndexedDB via `crateStoreIdb` (see
 * ../crateStore.js). IndexedDB is unbounded in practice (chromium
 * defaults to ~60 % of free disk), so a 10 k-song library is a
 * non-event. Consumers that want the async unbounded view should await
 * `loadStoredCratesAsync()` instead.
 */
const LS_KEY = "nd_serato_crates_v1";
const LS_CAP_CRATES = 100;              // was 25 — bumped so multi-source imports don't evict each other
const LS_TRACKS_PER_CRATE_INLINE = 50;   // only the first N tracks stay in localStorage for quick paint; full track list lives in IDB

// Lazy require the IDB helper — avoids a hard dep for consumers that just
// want the sync localStorage snapshot.
let _idbMod = null;
async function _idb() {
  if (_idbMod) return _idbMod;
  _idbMod = await import("../crateStore");
  return _idbMod;
}

export function loadStoredCrates() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

/** Async, unbounded view of the crate list. Prefer this in new UI code. */
export async function loadStoredCratesAsync() {
  try { return await (await _idb()).idbLoadCrates(); }
  catch { return loadStoredCrates(); }
}

export function saveCrates(crates) {
  // localStorage keeps only a slimmed snapshot (first N tracks per crate)
  // so the UI can render instantly on cold reload without hitting quota
  // for big libraries. The full track list lives in IndexedDB.
  const slim = crates.slice(0, LS_CAP_CRATES).map(c => ({
    ...c,
    tracks: (c.tracks || []).slice(0, LS_TRACKS_PER_CRATE_INLINE),
    total_tracks: (c.tracks || []).length,
  }));
  try { localStorage.setItem(LS_KEY, JSON.stringify(slim)); } catch { /* quota */ }
  // Fire-and-forget IDB write. Any UI awaiting the async loader will get
  // the full list once this settles.
  _idb().then(m => m.idbSaveCrates(crates)).catch(() => { /* IDB unavailable */ });
}

/** Replace one crate in the store while keeping the rest untouched. */
export function updateCrate(id, next) {
  const list = loadStoredCrates().map(c => c.id === id ? { ...c, ...next } : c);
  saveCrates(list);
  return list;
}

export function addCrate(name, tracks) {
  const list = loadStoredCrates();
  list.unshift({ id: Date.now().toString(36), name, tracks, imported_at: new Date().toISOString() });
  saveCrates(list);
  return list;
}

export function removeCrate(id) {
  const list = loadStoredCrates().filter(c => c.id !== id);
  saveCrates(list);
  return list;
}
